import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { trackEvent } from '../analytics';
import { stateToPlan, summarizePlan } from '../lib/capturePlan';
import { formatDurationInput } from '../lib/timelapseCalc';
import { createProjectWithPlan, saveCapturePlanToProject } from '../lib/saveCapturePlan';
import './SaveCalculationDialog.css';

// sessionStorage key: set before the sign-in modal opens so the calculator
// can re-open this dialog once the user comes back authenticated.
const SAVE_PENDING_KEY = 'tweedia:calcSavePending';

// A sensible default project name from the plan (e.g. "Timelapse — 6 months",
// "Driving timelapse — 30 mi"). Named after what was shot, since that is what
// the visitor will recognise in their project list a week later.
export function defaultProjectName(state) {
  if (state.scenario === 'drive') {
    return `Driving timelapse — ${state.distanceValue} ${state.distanceUnit || 'mi'}`;
  }
  return `Timelapse — ${formatDurationInput(state.durationValue, state.durationUnit)}`;
}

// Analytics params shared by both save paths — duration/frame numbers come
// straight from summarizePlan so they match the in-project card's events.
function planMetrics(state) {
  const summary = summarizePlan(stateToPlan(state));
  return {
    mode: state.mode,
    scenario: state.scenario,
    solve_for: state.solveFor,
    duration_days: summary ? summary.durationDays : null,
    frame_count: summary ? summary.frameCount : null,
  };
}

// Modal that converts a public-calculator estimate into a saved capturePlan —
// either by creating an account (signed-out) or saving into a new/existing
// project (signed-in). Launched from TimelapseCalculatorPage.
const SaveCalculationDialog = ({ state, user, onRequestSignIn, onClose }) => {
  const [tab, setTab] = useState('new');
  const [newName, setNewName] = useState(() => defaultProjectName(state));
  const [projects, setProjects] = useState([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedTo, setSavedTo] = useState(null);
  // Holds the id of a project that was created but whose plan write failed, so a
  // retry reuses it via the existing-project path instead of orphaning another.
  const createdProjectIdRef = useRef(null);

  // One-time fetch of the signed-in user's projects for the "Existing" picker.
  // getDocs (not onSnapshot) — the picker is a point-in-time snapshot.
  const loadProjects = useCallback(async () => {
    if (!user) return;
    setLoadingProjects(true);
    setError(null);
    try {
      // Mirror Studio.js: filter by userId only (single-field index, always
      // present) and sort newest-first client-side, so the picker never
      // depends on a userId+createdAt composite index that may not exist.
      const q = query(collection(db, 'projects'), where('userId', '==', user.uid));
      const snap = await getDocs(q);
      const rows = [];
      snap.forEach((d) => {
        const data = d.data();
        const createdAtMs = data.createdAt && data.createdAt.toMillis ? data.createdAt.toMillis() : 0;
        rows.push({ id: d.id, projectName: data.projectName, createdAtMs });
      });
      rows.sort((a, b) => b.createdAtMs - a.createdAtMs);
      setProjects(rows);
      setSelectedId(rows.length ? rows[0].id : '');
      setProjectsLoaded(true);
    } catch (e) {
      setError('Could not load your projects. Please try again.');
    } finally {
      setLoadingProjects(false);
    }
  }, [user]);

  const selectTab = (next) => {
    setError(null);
    setTab(next);
    if (next === 'existing' && !projectsLoaded && !loadingProjects) loadProjects();
  };

  // Signed-out primary: stash the intent, open the app sign-in modal, close.
  const handleSignIn = () => {
    trackEvent('cta_click', { cta_id: 'calc_save_signin', signed_in: false });
    try {
      sessionStorage.setItem(SAVE_PENDING_KEY, '1');
    } catch (e) {
      // sessionStorage can throw in private mode; sign-in still proceeds.
    }
    onRequestSignIn();
    onClose();
  };

  const finishSave = (projectName, target) => {
    trackEvent('capture_plan_saved', { source: 'calculator', target, ...planMetrics(state) });
    setSavedTo(projectName || 'your project');
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (tab === 'new') {
        const name = newName.trim();
        if (!name) { setSaving(false); return; }
        if (createdProjectIdRef.current) {
          // A prior attempt already created the project; only the plan write
          // failed. Reuse it so retrying doesn't create a second empty project —
          // and rename it to the current name so an edit between attempts stays
          // consistent with the "Saved to …" message.
          await saveCapturePlanToProject(createdProjectIdRef.current, state, { projectName: name });
        } else {
          try {
            await createProjectWithPlan(name, state);
          } catch (e) {
            if (e && e.projectId) createdProjectIdRef.current = e.projectId;
            throw e;
          }
        }
        createdProjectIdRef.current = null;
        finishSave(name, 'new');
      } else {
        if (!selectedId) { setSaving(false); return; }
        await saveCapturePlanToProject(selectedId, state);
        const picked = projects.find((p) => p.id === selectedId);
        finishSave(picked ? picked.projectName : null, 'existing');
      }
    } catch (e) {
      setError('Could not save your plan. Please try again.');
      setSaving(false);
    }
  };

  const trimmedName = newName.trim();
  const saveDisabled = saving
    || (tab === 'new' && !trimmedName)
    || (tab === 'existing' && (loadingProjects || !selectedId));

  return (
    <div className="cp-overlay" role="dialog" aria-modal="true" aria-label="Save calculation" data-testid="save-calc-dialog">
      <div className="cp-modal scd-modal">
        <div className="cp-modal-head">
          <div>
            <h3 className="cp-modal-title">Save this plan to a project</h3>
            <p className="cp-modal-sub">
              {savedTo
                ? 'Your capture plan is saved.'
                : user
                  ? 'Keep this estimate with a project so you can pick up where you left off.'
                  : 'Create a free account to turn this estimate into a real timelapse — and save as many plans as you like.'}
            </p>
          </div>
          <button type="button" className="cp-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="cp-modal-body">
          {savedTo ? (
            <div className="scd-success" data-testid="save-success">
              <p className="scd-success-msg">Saved to {savedTo}</p>
              <Link to="/studio" className="cp-btn cp-btn--primary scd-view-link" data-testid="save-view-studio">
                View in Studio →
              </Link>
            </div>
          ) : user ? (
            <>
              <div className="scd-tabs" role="tablist" aria-label="Save target">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'new'}
                  className={`scd-tab${tab === 'new' ? ' is-active' : ''}`}
                  onClick={() => selectTab('new')}
                  data-testid="save-tab-new"
                >
                  New project
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'existing'}
                  className={`scd-tab${tab === 'existing' ? ' is-active' : ''}`}
                  onClick={() => selectTab('existing')}
                  data-testid="save-tab-existing"
                >
                  Existing project
                </button>
              </div>

              {tab === 'new' ? (
                <label className="scd-field">
                  <span className="scd-field-label">Project name</span>
                  <input
                    type="text"
                    className="scd-input"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    data-testid="save-new-name"
                  />
                </label>
              ) : loadingProjects ? (
                <p className="scd-loading">Loading your projects…</p>
              ) : projects.length === 0 ? (
                <p className="scd-empty">
                  You don't have any projects yet —{' '}
                  <button type="button" className="scd-linkbtn" onClick={() => selectTab('new')}>
                    create one
                  </button>
                  .
                </p>
              ) : (
                <label className="scd-field">
                  <span className="scd-field-label">Choose a project</span>
                  <select
                    className="scd-input"
                    value={selectedId}
                    onChange={(e) => setSelectedId(e.target.value)}
                    data-testid="save-existing-select"
                  >
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.projectName}</option>
                    ))}
                  </select>
                </label>
              )}
            </>
          ) : (
            <div className="scd-value-prop">
              <ul className="scd-value-list">
                <li>Turn this estimate into a real timelapse project.</li>
                <li>Save as many capture plans as you like.</li>
                <li>Free — no credit card, no commitment.</li>
              </ul>
            </div>
          )}
        </div>

        {error && <div className="cp-modal-alert" role="alert" data-testid="save-error">{error}</div>}

        <div className="cp-modal-actions">
          {savedTo ? (
            <button type="button" className="cp-btn cp-btn--primary" onClick={onClose}>
              Done
            </button>
          ) : user ? (
            <>
              <button type="button" className="cp-btn cp-btn--ghost" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--primary"
                onClick={handleSave}
                disabled={saveDisabled}
                data-testid="save-submit"
              >
                {saving ? 'Saving…' : 'Save plan'}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="cp-btn cp-btn--ghost" onClick={onClose}>
                Maybe later
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--primary"
                onClick={handleSignIn}
                data-testid="save-signin-btn"
              >
                Create account &amp; save
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaveCalculationDialog;
